import React, { useCallback, useMemo } from 'react'
import { LinkButton } from '@/atoms/Button'
import { useGuildMember } from '@/services/GuildUserService'
import { VideoMetaData } from '@/services/ProjectsService'
import { Media, useFormatDuration } from '@/utils/EpisodeUtil'
import { useSafeTrack } from '@/utils/analytics'
import { useTranslate } from '@/utils/translate/translate-client'
import { BonusMediaCard } from './BonusMediaCard'
import { BonusVideoCard } from './BonusVideoCard'

interface BonusContentTabProps {
  projectSlug: string
  videos: VideoMetaData[]
  movieSpecialMedias: Media[]
  hasWatchableReasons: boolean
  pagePlacement: number
}

const MAX_BONUS_CONTENT_DISPLAY = 10

export const BonusContentTab: React.FC<BonusContentTabProps> = ({
  projectSlug,
  videos,
  movieSpecialMedias,
  hasWatchableReasons,
  pagePlacement,
}) => {
  const { t } = useTranslate('watch')
  const track = useSafeTrack()
  const { guildPermissions } = useGuildMember()
  const userHasEarlyAccess = Boolean(guildPermissions.hasEarlyAccess)
  const formatDuration = useFormatDuration(t)

  const handleClick = useCallback(
    (video: VideoMetaData) => {
      track('Watch Project Bonus Video Clicked', { videoId: video.guid })
    },
    [track],
  )

  const handleViewAllClick = useCallback(() => {
    track('Watch Project Bonus Content View All Clicked', { project: projectSlug })
  }, [track, projectSlug])

  const displayedVideos = useMemo(() => {
    return videos.slice(0, MAX_BONUS_CONTENT_DISPLAY)
  }, [videos])

  const displayedMovieSpecialMedias = useMemo(() => {
    const remainingSlots = MAX_BONUS_CONTENT_DISPLAY - displayedVideos.length
    return remainingSlots > 0 ? movieSpecialMedias.slice(0, remainingSlots) : []
  }, [movieSpecialMedias, displayedVideos.length])

  const totalBonusContent = videos.length + movieSpecialMedias.length
  const hasMoreBonusContent = totalBonusContent > MAX_BONUS_CONTENT_DISPLAY

  return (
    <div id="bonus-section" className="flex flex-col gap-y-10 md:gap-y-6 lg:gap-y-10">
      {displayedVideos.map((video, index) => {
        const durationLabel =
          typeof video.source.duration === 'number' ? formatDuration(video.source.duration) : undefined

        return (
          <BonusVideoCard
            key={video.guid}
            video={video}
            projectSlug={projectSlug}
            itemIndex={index}
            pagePlacement={pagePlacement}
            durationLabel={durationLabel}
            onVideoClick={handleClick}
            includeHoverScale={true}
          />
        )
      })}
      {displayedMovieSpecialMedias.map((media, i) => {
        const adjustedIndex = displayedVideos.length + i
        return (
          <BonusMediaCard
            className="relative flex min-h-[100px] w-full shrink-0 snap-center snap-always flex-row items-start gap-4"
            thumbnailClassName="h-full w-full min-w-[180px] md:min-w-[256px] lg:min-w-[300px]"
            {...media}
            key={media.guid}
            itemIndex={adjustedIndex}
            pagePlacement={pagePlacement}
            imgPriority={i <= 4}
            includeHoverScale={true}
            userHasEarlyAccess={userHasEarlyAccess}
            hasWatchableReasons={hasWatchableReasons}
            theaterEpisodeCard
          />
        )
      })}
      {hasMoreBonusContent && (
        <div className="flex justify-start pt-4">
          <LinkButton
            href={`/watch/${projectSlug}/bonus-content`}
            className="mb-16 w-full md:w-fit"
            variant="white"
            outline
            onClick={handleViewAllClick}
          >
            {t('viewAllBonusContent', 'View All Bonus Content')}
          </LinkButton>
        </div>
      )}
    </div>
  )
}
